<template>
  <v-col
    v-if="hasDocketwiseSettings"
    cols="auto"
  >
    <template v-if="partyInDocketwise && userHasDocketwise">
      <v-tooltip
        bottom
      >
        <template
          #activator="{ on, attrs }"
        >
          <v-btn
            v-bind="attrs"
            icon
            color="primary"
            v-on="on"
            @click="openDocketwiseParty"
          >
            <v-icon>
              policy
            </v-icon>
          </v-btn>
        </template>

        <span>Open Docketwise Contact</span>
      </v-tooltip>

      <v-tooltip
        bottom
      >
        <template
          #activator="{ on, attrs }"
        >
          <v-btn
            v-bind="attrs"
            icon
            color="secondary"
            v-on="on"
            @click="onUpdateContact"
          >
            <v-icon>
              cloud_sync
            </v-icon>
          </v-btn>
        </template>

        <span>Update Docketwise Contact</span>
      </v-tooltip>

      <v-tooltip
        bottom
      >
        <template
          #activator="{ on, attrs }"
        >
          <v-btn
            v-bind="attrs"
            icon
            color="error"
            v-on="on"
            @click="showDocketwiseRemoveDialog = true"
          >
            <v-icon>
              gpp_bad
            </v-icon>
          </v-btn>
        </template>

        <span>Remove Docketwise Contact</span>
      </v-tooltip>
    </template>

    <template v-else>
      <v-tooltip
        bottom
      >
        <template
          #activator="{ on, attrs }"
        >
          <v-btn
            v-bind="attrs"
            icon
            v-on="on"
            @click="onShowDialog"
          >
            <v-icon>
              ios_share
            </v-icon>
          </v-btn>
        </template>

        <span>Create Docketwise Contact</span>
      </v-tooltip>
    </template>

    <PEPartyDocketwiseCreateDialog
      v-model="showDocketwiseDialog"
      :title="partyFullName"
      :submitting="submitting"
      :on-submit="onCreateContact"
    />

    <PEPartyDocketwiseRemoveDialog
      v-model="showDocketwiseRemoveDialog"
      :title="partyFullName"
      :submitting="submitting"
      :on-submit="onRemoveContact"
    />
  </v-col>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities/error-responses'

import { INTEGRATION_NAMESPACE } from '@/store/integrations'
import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import PEPartyDocketwiseCreateDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyDocketwiseCreateDialog.vue'
import PEPartyDocketwiseRemoveDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyDocketwiseRemoveDialog.vue'

export default {
  name: 'DocketwiseButtons',

  components: {
    PEPartyDocketwiseCreateDialog,
    PEPartyDocketwiseRemoveDialog,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      showDocketwiseDialog: false,
      showDocketwiseRemoveDialog: false,
      submitting: false,
    }
  },

  computed: {
    hasDocketwiseSettings () {
      return this.features?.docketwise?.enabled
    },

    partyFullName () {
      const headerString = [this.peParty.first_name]
      if (this.peParty.middle_name) headerString.push(this.peParty.middle_name)
      headerString.push(this.peParty.last_name)
      headerString.push(`(ID: ${this.peParty.id})`)

      return headerString.join(' ')
    },

    partyInDocketwise () {
      return this.peParty?.docketwise_id !== null
    },

    userHasDocketwise () {
      return this.getCurrentUser ? !!this.getCurrentUser['docketwise_token?'] : false
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peParty',
      'peUserCanUpdate',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
    ]),

    ...mapGetters([
      'getCurrentUser',
    ]),
  },

  mounted () {
    this.getFeatures()
  },

  methods: {
    onCreateContact () {
      this.submitting = true
      this.docketwiseCreateContact(this.peParty.id).
        then(_ => {
          this.displayAlert('success', 'Party created in docketwise successfully')
          this.showDocketwiseDialog = false
          this.submitting = false
          this.peGetPartySection({ id: this.peParty.id })
        }).
        catch(e => this.displayAlert('error', parseErrorResponseMessage(e)))
    },

    onRemoveContact (deleteFromDocketwise) {
      this.submitting = true
      this.docketwiseRemoveContact({ deleteFromDocketwise, party: this.peParty }).
        then(_ => {
          this.displayAlert('success', 'Docketwise Contact removed successfully')
          this.showDocketwiseRemoveDialog = false
          this.submitting = false
          this.peGetPartySection({ id: this.peParty.id })
        }).
        catch(e => this.displayAlert('error', parseErrorResponseMessage(e)))
    },

    onShowDialog () {
      if (this.userHasDocketwise) {
        this.showDocketwiseDialog = true
      } else {
        this.$router.push({ name: 'int-docketwise-settings' })
      }
    },

    onUpdateContact () {
      this.docketwiseUpdateContact({ party: this.peParty }).
        then(_ => this.displayAlert('success', 'Party updated in docketwise successfully')).
        catch(e => this.displayAlert('error', parseErrorResponseMessage(e)))
    },

    openDocketwiseParty () {
      window.open(`https://app.docketwise.com/contacts/${this.peParty.docketwise_id}`, '_blank')
    },

    ...mapActions(INTEGRATION_NAMESPACE, [
      'docketwiseCreateContact',
      'docketwiseRemoveContact',
      'docketwiseUpdateContact',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'peGetPartySection',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getFeatures',
    ]),
  },
}
</script>
